import { Client } from '@microsoft/microsoft-graph-client';
import { TokenCredentialAuthenticationProvider } from '@microsoft/microsoft-graph-client/authProviders/azureTokenCredentials';

import { createApiRef } from '@backstage/core-plugin-api';

export const armApiRef = createApiRef<ArmApiClient>({
  id: 'plugin.kaap.armapi',
});

const WELL_KNOWN_GROUPS = [
  'dcedc4ca-ef7d-4e38-8f98-713505aa017b', // TomTom Users
  'b2f412ba-da2f-4adf-a171-8439a18822e7', // RG_AZ_GTMHUB_Access_Employees
  '5543ef50-dfb8-4209-9675-30633be0e91c', // RG_MS_LIC_M365_E5_Suite_End_User_Global_Standard
  '62376fb8-8ecd-4b5d-9283-f8c90855ce12', // RG_App_Library
  '97ed1212-89a3-45b8-8ab0-795197fb3bb2', // RG_AZ_OPEN_MAPS_Viewer_All_Employees_Contractors_Dev
  'cf7e0dca-ea6e-4851-a081-a0c9a4e82b2e', // RG_AZ_Allow_O365_Group_Creation
  'f5e93fdd-1c6f-475d-bea2-2c24ed90a8a1', // test_dynamic_allusercontractor
];

type Group = { id: string; name: string };

type GetGroupsParams = {
  ids?: string[];
  match?: string;
  isMember?: boolean;
};

export const groupDeserializer = (group: {
  id: string;
  displayName: string;
}): Group => {
  return { id: group.id, name: group.displayName };
};

export const groupFilter = (group: Group): boolean =>
  !WELL_KNOWN_GROUPS.includes(group.id.toLowerCase()) &&
  !group.name?.toUpperCase().startsWith('RG_AZ_PIM_') &&
  !group.name?.toUpperCase().startsWith('PG_CONCUR_SSO') &&
  !group.name?.toUpperCase().startsWith('RG_APP_') &&
  !group.name?.toUpperCase().startsWith('RG_AZ_APP_') &&
  !group.name?.toUpperCase().startsWith('RG_AZ_LOC_') &&
  !group.name?.toUpperCase().startsWith('RG_AZ_TT_') &&
  !group.name?.toUpperCase().startsWith('RG_VPN_') &&
  !group.name?.toUpperCase().startsWith('SPO_LOCATION_') &&
  !group.name?.toUpperCase().startsWith('RG_AZ_SLACK_USER_GROUP_') &&
  !group.name?.toUpperCase().startsWith('RG_AZ_CLOUDSPEND_READERS');

export class ArmApiClient {
  static buildGraphClient(
    authProvider: TokenCredentialAuthenticationProvider,
  ): Client {
    return Client.initWithMiddleware({
      authProvider,
      fetchOptions: {
        headers: { ConsistencyLevel: 'eventual' },
      },
    });
  }

  async getGroup(groupId: string, graphClient: Client): Promise<Group> {
    return groupDeserializer(await graphClient.api(`/groups/${groupId}`).get());
  }

  async getGroups(
    authProvider: TokenCredentialAuthenticationProvider,
    params: GetGroupsParams,
  ): Promise<Group[]> {
    const graphClient = Client.initWithMiddleware({
      authProvider,
      fetchOptions: {
        headers: { ConsistencyLevel: 'eventual' },
      },
    });

    let groups;
    if (!Boolean(params.ids)) {
      const path = Boolean(params.isMember)
        ? '/me/memberOf/$/microsoft.graph.group'
        : '/groups';
      let filter = 'mailEnabled eq false and securityEnabled eq true';
      if (Boolean(params.match))
        filter += ` and startswith(displayName, '${params.match}')`;
      groups = await graphClient
        .api(`${path}?$count=true`)
        .filter(filter)
        .select('id,displayName')
        .get();
    } else {
      groups = await graphClient
        .api('/directoryObjects/getByIds')
        .post({ ids: params.ids, type: ['group'] });
    }

    return groups.value.map(groupDeserializer).filter(groupFilter);
  }
}
